/**
 * LXRYWOOD — Scroll-Lock-Guard
 *
 * Problem das dieser Guard loest:
 * Das Theme sperrt den Seiten-Scroll ueber `body[scroll-y-off] { position: fixed }`
 * (siehe assets/global.js -> disablePageScroll und assets/style.css).
 * Entsperrt wird ausschliesslich in ModalDialog.hide(). Uebernimmt eine Drittanbieter-App
 * die Warenkorb-Oberflaeche (blendet den nativen Drawer aus und zeigt einen eigenen),
 * wird hide() nie aufgerufen -> das Attribut bleibt stehen -> die Seite ist dauerhaft
 * eingefroren, bis der Kunde neu laedt. Genau das ist im Live-Shop passiert
 * (Snap Cart Drawer, Add-to-Cart-Pfad).
 *
 * Strategie: rein defensiv, keine Aenderung am normalen Ablauf.
 * Solange der Lock gesetzt ist, wird geprueft ob ihn ueberhaupt noch jemand braucht.
 * Ist kein sichtbares Modal, Drawer, Sheet oder Menue offen, wird sauber entsperrt.
 * Laeuft NUR waehrend der Lock aktiv ist — im Normalbetrieb entstehen keine Kosten.
 *
 * Debug im Live-Shop: window.lxryScrollLockGuard.check() / .release()
 */
(function () {
  "use strict";

  var LOCK_ATTR = "scroll-y-off";

  /* Pruefintervall waehrend der Lock aktiv ist */
  var POLL_MS = 400;

  /* So oft muss "Lock unbegruendet" hintereinander zutreffen, bevor entsperrt wird.
     Puffer gegen Oeffnungs-Animationen (Theme-Drawer: slideIn 300ms). */
  var REQUIRED_STRIKES = 2;

  /* Ab welcher Groesse gilt ein fixiertes Element als blockierende Ebene.
     Seitliche Drawer sind schmal (30rem), aber hoch — Sticky-Header sind flach. */
  var MIN_HEIGHT_RATIO = 0.4;
  var MIN_WIDTH_RATIO = 0.2;

  /* Kandidaten fuer fremde/eigene Overlays ueber sprechende Namen. */
  var OVERLAY_CANDIDATES = [
    '[class*="backdrop"]',
    '[class*="overlay"]',
    '[class*="drawer"]',
    '[class*="sheet"]',
    '[class*="modal"]',
    '[class*="popup"]',
    '[id*="drawer"]',
    '[id*="modal"]',
    '[id*="overlay"]',
    '[id*="popup"]',
    '[id*="cart"]',
    "dialog[open]",
  ].join(",");

  /* Obergrenze fuer den strukturellen Scan — haelt die Pruefung auch auf sehr
     grossen Seiten billig. */
  var MAX_CANDIDATES = 400;

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, LINK: 1, NOSCRIPT: 1, TEMPLATE: 1 };

  var timer = null;
  var strikes = 0;

  function isVisible(el) {
    if (!el || typeof el.getClientRects !== "function") return false;
    if (el.getClientRects().length === 0) return false;

    var cs = window.getComputedStyle(el);
    if (!cs) return false;
    if (cs.display === "none") return false;
    if (cs.visibility === "hidden" || cs.visibility === "collapse") return false;
    if (parseFloat(cs.opacity) < 0.05) return false;

    var rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  /* Fixierte Ebene, die gross genug ist um den Scroll legitim zu blockieren.
     Entscheidend ist der Anteil, der WIRKLICH im Viewport liegt: geschlossene
     Drawer werden meist per transform: translateX(100%) aus dem Bild geschoben
     statt ausgeblendet. Ohne Viewport-Schnitt wuerde so ein zugeschobener Drawer
     als offene Ebene zaehlen und der Guard wuerde nie entsperren. */
  function isBlockingOverlay(el) {
    if (!isVisible(el)) return false;

    var cs = window.getComputedStyle(el);
    if (cs.position !== "fixed") return false;
    if (cs.pointerEvents === "none") return false;

    var rect = el.getBoundingClientRect();
    var visibleWidth =
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
    var visibleHeight =
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

    if (visibleWidth <= 0 || visibleHeight <= 0) return false;

    return (
      visibleHeight >= window.innerHeight * MIN_HEIGHT_RATIO &&
      visibleWidth >= window.innerWidth * MIN_WIDTH_RATIO
    );
  }

  /**
   * Elemente, die ueberhaupt als blockierende Ebene in Frage kommen.
   * Neben sprechenden Klassen/IDs auch strukturell: Drittanbieter-Drawer haengen
   * fast immer direkt am <body> und tragen oft keinen erkennbaren Namen.
   */
  function collectOverlayCandidates() {
    var list = [];
    var named = document.querySelectorAll(OVERLAY_CANDIDATES);
    var i;

    for (i = 0; i < named.length && list.length < MAX_CANDIDATES; i++) {
      list.push(named[i]);
    }

    var children = document.body.children;
    for (i = 0; i < children.length && list.length < MAX_CANDIDATES; i++) {
      var child = children[i];
      if (SKIP_TAGS[child.tagName]) continue;

      list.push(child);

      /* Theme-Inhalt nicht weiter durchsuchen — dort sitzen keine App-Drawer */
      if (child.classList.contains("shopify-section")) continue;

      var nested = child.children;
      for (var j = 0; j < nested.length && list.length < MAX_CANDIDATES; j++) {
        if (SKIP_TAGS[nested[j].tagName]) continue;
        list.push(nested[j]);
      }
    }

    return list;
  }

  /**
   * Braucht den Lock ueberhaupt noch jemand?
   * true  = es ist etwas offen, Finger weg
   * false = niemand haelt den Lock, er ist ein Leak
   */
  function isLockJustified() {
    /* 1. Theme-Modals und -Drawer (ModalDialog / Drawer / cart-drawer) */
    var modals = document.querySelectorAll(".modal[open]");
    for (var i = 0; i < modals.length; i++) {
      if (isVisible(modals[i])) return true;
    }

    /* 2. Header-Menue-Drawer (HeaderDrawer setzt .menu-open) */
    var menus = document.querySelectorAll("details.menu-open");
    for (var j = 0; j < menus.length; j++) {
      if (isVisible(menus[j])) return true;
    }

    /* 3. Beliebige fremde oder eigene Overlay-Ebene (App-Drawer, LP-Sheets,
          Cookie-Banner, Lightboxen) */
    var candidates = collectOverlayCandidates();
    for (var k = 0; k < candidates.length; k++) {
      if (isBlockingOverlay(candidates[k])) return true;
    }

    return false;
  }

  /* Manueller Fallback, falls global.js nicht geladen ist */
  function forceUnlock() {
    var body = document.body;
    var top = parseInt(body.style.top, 10);
    var scrollPosition = isNaN(top) ? 0 : -top;

    body.style.top = "";
    body.style.position = "";
    body.style.width = "";
    body.removeAttribute(LOCK_ATTR);

    window.requestAnimationFrame(function () {
      window.scrollTo(0, scrollPosition);
    });
  }

  function release() {
    /* Zuerst die Theme-Modals sauber schliessen, die zwar [open] sind aber
       nicht sichtbar (von einer App ausgeblendet). hide() raeumt Fokus-Trap,
       aria-expanded und Medien mit auf — und entsperrt selbst. */
    var modals = document.querySelectorAll(".modal[open]");
    for (var i = 0; i < modals.length; i++) {
      var modal = modals[i];
      if (isVisible(modal)) continue;
      if (typeof modal.hide !== "function") continue;

      try {
        modal.hide();
      } catch (e) {
        /* Ein defektes Modal darf die Entsperrung unten nicht verhindern */
      }
    }

    if (!document.body.hasAttribute(LOCK_ATTR)) return;

    if (typeof window.enablePageScroll === "function") window.enablePageScroll();
    else forceUnlock();

    /* Letzte Sicherung: hat enablePageScroll nicht gegriffen, hart aufraeumen */
    if (document.body.hasAttribute(LOCK_ATTR)) forceUnlock();
  }

  function stop() {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
  }

  function check() {
    if (!document.body.hasAttribute(LOCK_ATTR)) {
      strikes = 0;
      stop();
      return;
    }

    if (isLockJustified()) {
      strikes = 0;
      return;
    }

    strikes++;
    if (strikes < REQUIRED_STRIKES) return;

    strikes = 0;
    release();
    stop();
  }

  function start() {
    if (timer !== null) return;
    strikes = 0;
    timer = window.setInterval(check, POLL_MS);
  }

  function onLockAttributeChange() {
    if (document.body.hasAttribute(LOCK_ATTR)) start();
    else stop();
  }

  function init() {
    if (!document.body) return;

    new MutationObserver(onLockAttributeChange).observe(document.body, {
      attributes: true,
      attributeFilter: [LOCK_ATTR],
    });

    /* Zurueck-Navigation aus dem bfcache stellt den alten DOM-Zustand wieder her,
       inklusive eines eingefrorenen Body. */
    window.addEventListener("pageshow", function (event) {
      if (event.persisted) check();
    });

    onLockAttributeChange();
  }

  window.lxryScrollLockGuard = {
    check: check,
    release: release,
    isLockJustified: isLockJustified,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
